// DunDial — per-decline-code Stripe retry tuning for sub-$30K MRR SaaS.
//
// Route structure (React Router v6 pathless layout routes):
//
//   /login                → SignInPage  (no auth required)
//   RequireAuth guard     → redirects to /login on 401
//     /connect            → ConnectStripePage (auth required, no integration required)
//     IntegrationGate     → redirects to /connect when no active Stripe integration
//       AppShellLayout    → renders AppShell with sidebar + topbar
//         /               → DashboardPage
//         /recoveries     → RecoveriesPage
//         /failed-invoices → FailedPaymentsPage
//   *                     → Navigate to /

import { useEffect } from "react";
import { Navigate, Outlet, Route, Routes, NavLink } from "react-router-dom";
import { CreditCard, LayoutDashboard, RefreshCw } from "lucide-react";
import { AppShell } from "@avagency/frontend/components/shell/AppShell";
import { RequireAuth } from "@avagency/frontend/components/auth/RequireAuth";
import { SignInPage } from "./pages/SignInPage";
import { ConnectStripePage } from "./pages/ConnectStripePage";
import { DashboardPage } from "./pages/DashboardPage";
import { RecoveriesPage } from "./pages/RecoveriesPage";
import { FailedPaymentsPage } from "./pages/FailedPaymentsPage";
import { IntegrationGate } from "./components/IntegrationGate";
import { PrivacyPage } from "./pages/PrivacyPage";
import { TermsPage } from "./pages/TermsPage";

export default function App() {
  useEffect(() => {
    document.title = "DunDial";
  }, []);

  return (
    <Routes>
      {/* Public */}
      <Route path="/login" element={<SignInPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/terms" element={<TermsPage />} />

      {/* Authenticated */}
      <Route element={<RequireAuth redirectTo="/login" />}>
        <Route path="/connect" element={<ConnectStripePage />} />

        {/* Requires active Stripe integration */}
        <Route element={<IntegrationGate />}>
          <Route element={<AppShellLayout />}>
            <Route path="/" element={<DashboardPage />} />
            <Route path="/recoveries" element={<RecoveriesPage />} />
            <Route path="/failed-invoices" element={<FailedPaymentsPage />} />
          </Route>
        </Route>
      </Route>

      {/* Catch-all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

// AppShellLayout — pathless layout route that wraps the AppShell around
// whichever page the router selects via <Outlet />.
function AppShellLayout() {
  return (
    <AppShell productSlug="dundial" topbar={<TopbarBrand />} sidebar={<Sidebar />}>
      <Outlet />
    </AppShell>
  );
}

function TopbarBrand() {
  return (
    <div className="flex items-center gap-2.5">
      {/* Brand orb */}
      <div
        aria-hidden
        className="h-6 w-6 flex-shrink-0 rounded-full"
        style={{
          background: "conic-gradient(from 220deg, #f4f6fa, #8b94a6 30%, #1a2647 55%, #f4f6fa 80%, #cfd6e2)",
          boxShadow: "inset 0 0 0 1px rgba(255,255,255,.2), 0 2px 8px rgba(139,108,239,.3)",
        }}
      />
      <span className="dundial-wordmark">DunDial</span>
      <span className="dundial-wordmark-sub hidden sm:inline">/ Stripe dunning</span>
    </div>
  );
}

function Sidebar() {
  const navItemClass = (isActive: boolean) =>
    [
      "flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm font-medium transition-all duration-150",
      isActive
        ? "bg-primary text-primary-foreground dundial-pill-active"
        : "text-muted-foreground hover:bg-accent/60 hover:text-foreground",
    ].join(" ");

  return (
    <div className="space-y-0.5 pt-1">
      {/* Eyebrow label */}
      <p
        className="mb-3 px-3 font-mono text-[10px] uppercase tracking-widest text-muted-foreground/60"
        aria-hidden
      >
        Navigation
      </p>
      <ul className="space-y-0.5" role="list">
        <li>
          <NavLink to="/" end className={({ isActive }) => navItemClass(isActive)}>
            <LayoutDashboard className="h-4 w-4 flex-shrink-0" aria-hidden />
            Dashboard
          </NavLink>
        </li>
        <li>
          <NavLink
            to="/recoveries"
            className={({ isActive }) => navItemClass(isActive)}
          >
            <RefreshCw className="h-4 w-4 flex-shrink-0" aria-hidden />
            Recoveries
          </NavLink>
        </li>
        <li>
          <NavLink
            to="/failed-invoices"
            className={({ isActive }) => navItemClass(isActive)}
          >
            <CreditCard className="h-4 w-4 flex-shrink-0" aria-hidden />
            Failed Payments
          </NavLink>
        </li>
      </ul>
    </div>
  );
}
